import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/select';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../ui/tooltip';
import { AlertCircle, AlertTriangle, CheckCircle2, XCircle, Info, Trash2, ArrowDown, Wand2 } from 'lucide-react';
import BitVisualization from './BitVisualization';
import { dlcOptions, payloadLengthFor, parseHexBytes, formatBytes, CATEGORY_LABELS } from '../../../lib/canFrameDecoder';

export const FieldLabel = ({ children, tip, htmlFor }) => (
  <div className="flex items-center gap-1.5">
    <Label htmlFor={htmlFor} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">{children}</Label>
    {tip && (
      <Tooltip>
        <TooltipTrigger asChild>
          <Info className="h-3.5 w-3.5 text-muted-foreground/70 cursor-help" />
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs">{tip}</TooltipContent>
      </Tooltip>
    )}
  </div>
);

export const ErrorText = ({ children, testId }) => (
  <p className="flex items-start gap-1 text-xs text-destructive" data-testid={testId}>
    <AlertCircle className="h-3 w-3 shrink-0 mt-0.5" /> <span>{children}</span>
  </p>
);

export const SubSection = ({ title, children, testId }) => (
  <div className="space-y-4 p-4 bg-muted/50 rounded-md min-w-0" data-testid={testId}>
    <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</h3>
    <div className="space-y-4">{children}</div>
  </div>
);

export const FlowArrow = () => (
  <div className="flex justify-center text-muted-foreground/60" aria-hidden="true">
    <ArrowDown className="h-4 w-4" />
  </div>
);

export const Stat = ({ label, value, sub, testId, big }) => (
  <div className="rounded-md border border-structure bg-background p-3 min-w-0">
    <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className={`font-mono font-bold break-all ${big ? 'text-xl text-autoxium-blue' : 'text-sm'}`} data-testid={testId}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground normal-case">{sub}</div>}
  </div>
);

const RawFrameCard = ({ frame, index, result, siblings, issues, onChange, onSignalChange, onRemove, canRemove }) => {
  const n = index + 1;
  const tid = `cfd-frame-${n}`;
  const fe = result?.fieldErrors || {};
  const sig = frame.signal;
  const payload = payloadLengthFor(frame.protocol, frame.dlc);
  const bytes = parseHexBytes(frame.data);
  const decode = result?.decode;
  const layout = result?.layout;
  const errorCount = issues.filter((i) => i.severity === 'error').length;

  const field = (key, label, { tip, placeholder, mono = true, signal = false, type = 'text', inputMode } = {}) => {
    const errKey = signal ? `signal.${key}` : key;
    const id = `${tid}-${key}`;
    const value = signal ? sig[key] : frame[key];
    return (
      <div className="space-y-2 min-w-0">
        <FieldLabel htmlFor={id} tip={tip}>{label}</FieldLabel>
        <Input
          id={id} type={type} inputMode={inputMode} value={value} placeholder={placeholder}
          onChange={(e) => (signal ? onSignalChange({ [key]: e.target.value }) : onChange({ [key]: e.target.value }))}
          className={mono ? 'font-mono' : undefined}
          aria-invalid={!!fe[errKey]} data-testid={`${id}-input`}
        />
        {fe[errKey] && <ErrorText testId={`${id}-error`}>{fe[errKey]}</ErrorText>}
      </div>
    );
  };

  const select = (key, label, options, { signal = false, tip, onValue } = {}) => {
    const errKey = signal ? `signal.${key}` : key;
    const id = `${tid}-${key}`;
    const value = signal ? sig[key] : frame[key];
    const set = onValue || ((v) => (signal ? onSignalChange({ [key]: v }) : onChange({ [key]: v })));
    return (
      <div className="space-y-2 min-w-0">
        <FieldLabel tip={tip}>{label}</FieldLabel>
        <Select value={value} onValueChange={set}>
          <SelectTrigger data-testid={`${id}-select`} aria-label={label}><SelectValue /></SelectTrigger>
          {/* A fixed max height keeps Radix's popper from re-measuring in a loop while its
              scroll buttons appear/disappear on long lists (the 16-entry CAN FD DLC list),
              which raised "ResizeObserver loop completed with undelivered notifications". */}
          <SelectContent className="max-h-60">
            {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {fe[errKey] && <ErrorText testId={`${id}-error`}>{fe[errKey]}</ErrorText>}
      </div>
    );
  };

  const setProtocol = (protocol) => {
    const patch = { protocol };
    if (protocol === 'can' && Number(frame.dlc) > 8) patch.dlc = '8';
    onChange(patch);
  };

  const fitData = () => {
    if (payload === null || bytes === null) return;
    const fitted = bytes.slice(0, payload);
    while (fitted.length < payload) fitted.push(0);
    onChange({ data: formatBytes(fitted) });
  };

  const autoPlaceholder = (which) => (result?.minMaxAuto?.[which] && result[which] !== null && result[which] !== undefined
    ? `auto (${result[which]})` : 'auto');

  return (
    <Card className="technical-card scroll-mt-24" id={`raw-frame-${frame.id}`} data-testid={tid}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="font-chivo uppercase tracking-tight text-base flex flex-wrap items-center gap-2">
            Raw Frame {n}
            <span className="font-mono text-sm normal-case text-muted-foreground">
              {frame.messageName || 'Unnamed'} · {result?.canIdHex || frame.canId || '—'}
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            {errorCount > 0 ? (
              <Badge variant="destructive" data-testid={`${tid}-issue-badge`}>{errorCount} issue{errorCount === 1 ? '' : 's'}</Badge>
            ) : result && !result.inDbc ? null : result ? (
              <Badge variant="outline" className="text-emerald-700 border-emerald-600 dark:text-emerald-400">In DBC</Badge>
            ) : null}
            {canRemove && (
              <Button type="button" variant="ghost" size="sm" onClick={onRemove} aria-label={`Remove Raw Frame ${n}`} data-testid={`${tid}-remove`}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <SubSection title="1. Raw CAN Frame" testId={`${tid}-raw`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {field('canId', 'CAN ID (hex)', { placeholder: '0x180', tip: 'Hexadecimal, with or without 0x. Standard IDs go up to 0x7FF, extended up to 0x1FFFFFFF. Frames with the same CAN ID and ID format become one DBC message with one signal each.' })}
            {select('idFormat', 'Standard / Extended', [
              { value: 'standard', label: 'Standard (11-bit)' },
              { value: 'extended', label: 'Extended (29-bit)' },
            ])}
            {select('protocol', 'CAN / CAN FD', [
              { value: 'can', label: 'Classical CAN' },
              { value: 'canfd', label: 'CAN FD' },
            ], { onValue: setProtocol, tip: 'Classical CAN carries 0-8 bytes. CAN FD DLC 9-15 map to 12, 16, 20, 24, 32, 48 and 64 bytes.' })}
            {select('dlc', 'DLC', dlcOptions(frame.protocol), { tip: 'Data Length Code. The raw data must have exactly the payload length this DLC stands for.' })}
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <FieldLabel htmlFor={`${tid}-data`} tip="Hex bytes separated by spaces, commas or colons (10 27 00 ...), or one contiguous hex string (102700...).">Raw CAN Data</FieldLabel>
              <div className="flex items-center gap-2">
                <span
                  className={`font-mono text-xs ${bytes !== null && payload !== null && bytes.length !== payload ? 'text-destructive' : 'text-muted-foreground'}`}
                  data-testid={`${tid}-byte-count`}
                >
                  {bytes === null ? 'not valid hex' : `${bytes.length} / ${payload ?? '?'} bytes`}
                </span>
                {bytes !== null && payload !== null && bytes.length !== payload && (
                  <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={fitData} data-testid={`${tid}-fit`}>
                    <Wand2 className="h-3 w-3" /> Fit to DLC
                  </Button>
                )}
              </div>
            </div>
            <Input
              id={`${tid}-data`} value={frame.data} onChange={(e) => onChange({ data: e.target.value })}
              className="font-mono" placeholder="10 27 00 00 00 00 00 00" spellCheck={false} autoComplete="off"
              aria-invalid={!!fe.data} data-testid={`${tid}-data-input`}
            />
            {fe.data && <ErrorText testId={`${tid}-data-error`}>{fe.data}</ErrorText>}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('messageName', 'Message Name', { placeholder: 'VehicleData', tip: 'DBC identifier: letters, digits and _, not starting with a digit.' })}
            {field('transmitter', 'Transmitter ECU', { placeholder: 'ECU_VCU (optional)', tip: 'Sending node. Left blank, the DBC uses Vector__XXX (no node).' })}
          </div>
        </SubSection>

        <FlowArrow />

        <SubSection title="2. Signal Definition" testId={`${tid}-signal`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {field('name', 'Signal Name', { signal: true, placeholder: 'VehicleSpeed' })}
            {field('startBit', 'Start Bit', {
              signal: true, inputMode: 'numeric',
              tip: 'DBC start bit (bit = byte × 8 + bit-in-byte). Intel: position of the signal\'s LSB. Motorola: position of the signal\'s MSB, as in the SG_ line of a DBC file.',
            })}
            {field('length', 'Length (bits)', { signal: true, inputMode: 'numeric', tip: '1 to 64 bits.' })}
            {select('byteOrder', 'Byte Order', [
              { value: 'intel', label: 'Intel (Little Endian)' },
              { value: 'motorola', label: 'Motorola (Big Endian)' },
            ], { signal: true })}
            {select('valueType', 'Signed / Unsigned', [
              { value: 'unsigned', label: 'Unsigned (+)' },
              { value: 'signed', label: 'Signed (−, two\'s complement)' },
            ], { signal: true })}
            {field('factor', 'Factor', { signal: true, inputMode: 'decimal', tip: 'Physical = Raw × Factor + Offset. Must not be 0.' })}
            {field('offset', 'Offset', { signal: true, inputMode: 'decimal' })}
            {field('unit', 'Unit', { signal: true, mono: false, placeholder: 'km/h', tip: 'Printable ASCII without double quotes; write degC rather than °C for DBC compatibility.' })}
            {field('min', 'Min', { signal: true, inputMode: 'decimal', placeholder: autoPlaceholder('min'), tip: 'Physical minimum. Blank = the lowest value the raw bits can represent.' })}
            {field('max', 'Max', { signal: true, inputMode: 'decimal', placeholder: autoPlaceholder('max'), tip: 'Physical maximum. Blank = the highest value the raw bits can represent.' })}
            <div className="sm:col-span-2">
              {field('receiver', 'Receiver ECU', { signal: true, placeholder: 'ECU_BMS (optional; comma-separate several)', tip: 'Receiving node(s). Left blank, the DBC uses Vector__XXX.' })}
            </div>
          </div>
        </SubSection>

        <FlowArrow />

        <SubSection title="3. Decode" testId={`${tid}-decode`}>
          {!decode ? (
            <p className="text-sm text-muted-foreground" data-testid={`${tid}-decoding`}>Decoding…</p>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {decode.status === 'valid' ? (
                  <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white gap-1" data-testid={`${tid}-status`}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> Valid
                  </Badge>
                ) : (
                  <Badge variant="destructive" className="gap-1" data-testid={`${tid}-status`}>
                    <XCircle className="h-3.5 w-3.5" /> Invalid
                  </Badge>
                )}
                {layout && <span className="font-mono text-xs text-muted-foreground" data-testid={`${tid}-bit-range`}>{layout.text}</span>}
              </div>
              {decode.reasons.length > 0 && (
                <ul className="space-y-1">
                  {decode.reasons.map((r) => (
                    <li key={r} className="flex items-start gap-1.5 text-xs text-destructive"><AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {r}</li>
                  ))}
                </ul>
              )}
              {decode.decoded && (
                <>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <Stat
                      big label="Physical Value"
                      value={<>{decode.physicalText}{decode.unit && <span className="normal-case text-base font-normal"> {decode.unit}</span>}</>}
                      testId={`${tid}-physical`}
                    />
                    <Stat label="Raw Value" value={decode.raw} sub={decode.raw !== decode.rawUnsigned ? `unsigned ${decode.rawUnsigned}` : undefined} testId={`${tid}-raw-value`} />
                    <Stat label="Hex Value" value={decode.rawHex} testId={`${tid}-hex`} />
                    <Stat label="Bit Range" value={layout ? `bits ${Math.min(layout.lsb.dbcBit, layout.msb.dbcBit)}–${Math.max(layout.lsb.dbcBit, layout.msb.dbcBit)}` : '—'} sub={layout ? `byte${layout.bytes.length > 1 ? 's' : ''} ${layout.bytes.join(', ')} · start bit = ${layout.startBitRefersTo}` : undefined} />
                  </div>
                  <div className="rounded-md border border-structure bg-background p-3 overflow-x-auto">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Decoding Calculation</div>
                    <ol className="space-y-1 font-mono text-xs list-decimal list-inside" data-testid={`${tid}-steps`}>
                      {decode.steps.map((s) => <li key={s} className="break-words">{s}</li>)}
                    </ol>
                  </div>
                </>
              )}
            </div>
          )}
        </SubSection>

        <FlowArrow />

        <SubSection title="4. Bit Visualization" testId={`${tid}-bits-section`}>
          {layout ? (
            <BitVisualization
              payloadLength={result?.payloadLength ?? payload}
              dataBytes={result?.dataBytes ?? null}
              layout={layout}
              siblings={siblings}
              testId={`${tid}-bits`}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Enter a valid start bit, length and byte order to see the bit layout.</p>
          )}
        </SubSection>

        {issues.length > 0 && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-1.5" data-testid={`${tid}-issues`}>
            <div className="font-mono text-[10px] uppercase tracking-wider text-destructive">DBC validation issues for this frame</div>
            {issues.map((i, k) => (
              <div key={k} className={`flex items-start gap-1.5 text-xs ${i.severity === 'error' ? 'text-destructive' : 'text-amber-700 dark:text-amber-400'}`}>
                {i.severity === 'error' ? <XCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> : <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />}
                <span><span className="font-semibold">{CATEGORY_LABELS[i.category] || i.category}:</span> {i.message}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RawFrameCard;
